<template>
  <section class="poll-comments" id="poll-comments">
    <h2 class="poll-comments__title">Argumentări</h2>
    <p v-if="totalComments > 0" class="poll-comments__count">
      {{ totalComments === 1 ? 'Este 1 argumentare' : `Sunt ${totalComments} argumentări` }} de la votanți la acest sondaj.
    </p>

    <!-- Loading -->
    <div v-if="loadingComments" class="poll-comments__state">Se încarcă argumentările...</div>

    <!-- Fetch error -->
    <div v-else-if="fetchCommentsError" class="poll-comments__state poll-comments__fetch-error">
      Nu s-au putut încărca argumentările.
      <button type="button" class="poll-comments__retry-btn" @click="fetchComments">Reîncearcă</button>
    </div>

    <!-- Active poll -->
    <template v-else-if="pollIsActive">

      <!-- Server says user hasn't voted yet — single notice, no other elements -->
      <div v-if="userHasVoted === false" class="poll-comments__info-banner">
        <span class="poll-comments__info-icon" aria-hidden="true"><i class="mdi mdi-information"></i></span>
        <p class="poll-comments__info-text">
          Votează pentru a putea vedea și participa la argumentări.
        </p>
      </div>

      <!-- User has voted: editor + own comments + footer -->
      <template v-else>

        <!-- Comment editor (only if user has no top-level comment yet) -->
        <div v-if="!userHasComment" class="poll-comments__form-wrap">
          <div class="poll-comments__form">
            <RichTextEditor ref="richEditor" v-model="newCommentText" />
            <div class="poll-comments__form-actions">
              <button
                type="button"
                class="poll-comments__submit-btn"
                :disabled="isSubmitting || isTextEmpty(newCommentText)"
                @click="submitComment(null)"
              >
                {{ isSubmitting ? 'Se trimite...' : 'Publică argumentarea' }}
              </button>
            </div>
            <p v-if="submitError && !replyTarget" class="poll-comments__error">{{ submitError }}</p>
          </div>
        </div>

        <!-- All comments list -->
        <div v-if="comments.length > 0" class="poll-comments__list">
          <div v-for="comment in comments" :key="comment.id" class="poll-comments__thread">
            <!-- Top-level comment -->
            <CommentItem
              :comment="comment"
              :poll-id="pollId"
              :can-reply="pollIsActive"
              :can-edit="canEditComment(comment)"
              :can-delete="canDeleteComment(comment)"
              :is-own="comment.user_id === currentUserId"
              :current-user-id="currentUserId"
              :is-reported-by-me="comment.is_reported_by_me || false"
              :show-reply-editor="replyTarget && replyTarget.id === comment.id"
              @reply="startReply"
              @edited="onCommentEdited"
              @deleted="onCommentDeleted"
              @reported="onCommentReported($event, 'top')"
              @need-auth="$emit('need-auth')"
            >
              <template v-if="replyTarget && replyTarget.id === comment.id" #reply-editor>
                <div class="poll-comments__reply-form">
                  <div class="poll-comments__reply-quote">
                    {{ replyTarget.preview }}
                  </div>
                  <RichTextEditor v-model="replyText" />
                  <div class="poll-comments__form-actions">
                    <button type="button" class="poll-comments__cancel-btn" @click="cancelReply">Anulează</button>
                    <button
                      type="button"
                      class="poll-comments__submit-btn"
                      :disabled="isSubmitting || isTextEmpty(replyText)"
                      @click="submitReply(comment.id)"
                    >
                      {{ isSubmitting ? 'Se trimite...' : 'Publică răspunsul' }}
                    </button>
                  </div>
                  <p v-if="submitError" class="poll-comments__error">{{ submitError }}</p>
                </div>
              </template>
            </CommentItem>

            <!-- Lazy replies section -->
            <div class="poll-comments__replies-section">
              <button
                v-if="!repliesState[comment.id] && comment.replies_count > 0"
                type="button"
                class="poll-comments__load-replies-btn"
                @click="loadReplies(comment.id)"
              >
                ↳ Vezi {{ comment.replies_count }} {{ comment.replies_count === 1 ? 'răspuns' : 'răspunsuri' }}
              </button>

              <template v-if="repliesState[comment.id]">
                <div
                  v-if="repliesState[comment.id].loading"
                  class="poll-comments__state poll-comments__state--inline"
                >
                  Se încarcă răspunsurile...
                </div>
                <div
                  v-else-if="repliesState[comment.id].error"
                  class="poll-comments__replies-error"
                >
                  Nu s-au putut încărca răspunsurile.
                  <button type="button" class="poll-comments__retry-btn" @click="loadReplies(comment.id)">Reîncearcă</button>
                </div>
                <template v-else>
                  <div
                    v-if="repliesState[comment.id].replies.length === 0"
                    class="poll-comments__state poll-comments__state--inline"
                  >
                    Nu există răspunsuri momentan.
                  </div>
                  <CommentItem
                    v-for="reply in repliesState[comment.id].replies"
                    :key="reply.id"
                    :comment="reply"
                    :poll-id="pollId"
                    :can-reply="pollIsActive"
                    :is-reply="true"
                    :can-delete="canDeleteComment(reply)"
                    :is-own="reply.user_id === currentUserId"
                    :current-user-id="currentUserId"
                    :is-reported-by-me="reply.is_reported_by_me || false"
                    :show-reply-editor="replyTarget && replyTarget.id === reply.id"
                    @reply="startReply"
                    @deleted="onCommentDeleted"
                    @reported="onCommentReported($event, 'reply', comment.id)"
                    @need-auth="$emit('need-auth')"
                  >
                    <template v-if="replyTarget && replyTarget.id === reply.id" #reply-editor>
                      <div class="poll-comments__reply-form">
                        <div class="poll-comments__reply-quote">
                          {{ replyTarget.preview }}
                        </div>
                        <RichTextEditor v-model="replyText" />
                        <div class="poll-comments__form-actions">
                          <button type="button" class="poll-comments__cancel-btn" @click="cancelReply">Anulează</button>
                          <button
                            type="button"
                            class="poll-comments__submit-btn"
                            :disabled="isSubmitting || isTextEmpty(replyText)"
                            @click="submitReply(comment.id)"
                          >
                            {{ isSubmitting ? 'Se trimite...' : 'Publică răspunsul' }}
                          </button>
                        </div>
                        <p v-if="submitError" class="poll-comments__error">{{ submitError }}</p>
                      </div>
                    </template>
                  </CommentItem>
                </template>
              </template>
            </div>
          </div>
        </div>

        <!-- Footer: no-arguments message -->
        <div class="poll-comments__active-footer">
          <p v-if="totalComments === 0" class="poll-comments__state">
            Nu există nicio argumentare. Fii primul care argumentează!
          </p>
        </div>

      </template>
    </template>

    <!-- Finished poll: show all comments (existing behaviour) -->
    <template v-else>
      <!-- No comments -->
      <div v-if="comments.length === 0" class="poll-comments__state">
        Nicio argumentare încă. Fii primul care argumentează!
      </div>

      <!-- Comment list -->
      <div v-else class="poll-comments__list">
        <div v-for="comment in comments" :key="comment.id" class="poll-comments__thread">
          <!-- Top-level comment -->
          <CommentItem
            :comment="comment"
            :poll-id="pollId"
            :can-reply="hasVoted && pollIsActive"
            :can-edit="canEditComment(comment)"
            :can-delete="canDeleteComment(comment)"
            :is-own="comment.user_id === currentUserId"
            :current-user-id="currentUserId"
            :is-reported-by-me="comment.is_reported_by_me || false"
            :show-reply-editor="replyTarget && replyTarget.id === comment.id"
            @reply="startReply"
            @edited="onCommentEdited"
            @deleted="onCommentDeleted"
            @reported="onCommentReported($event, 'top')"
            @need-auth="$emit('need-auth')"
          >
            <template v-if="replyTarget && replyTarget.id === comment.id" #reply-editor>
              <div class="poll-comments__reply-form">
                <div class="poll-comments__reply-quote">
                  {{ replyTarget.preview }}
                </div>
                <RichTextEditor v-model="replyText" />
                <div class="poll-comments__form-actions">
                  <button type="button" class="poll-comments__cancel-btn" @click="cancelReply">Anulează</button>
                  <button
                    type="button"
                    class="poll-comments__submit-btn"
                    :disabled="isSubmitting || isTextEmpty(replyText)"
                    @click="submitReply(comment.id)"
                  >
                    {{ isSubmitting ? 'Se trimite...' : 'Publică răspunsul' }}
                  </button>
                </div>
                <p v-if="submitError" class="poll-comments__error">{{ submitError }}</p>
              </div>
            </template>
          </CommentItem>

          <!-- Lazy replies section -->
          <div class="poll-comments__replies-section">
            <!-- "Vezi N răspunsuri" button — only while not yet loaded -->
            <button
              v-if="!repliesState[comment.id] && comment.replies_count > 0"
              type="button"
              class="poll-comments__load-replies-btn"
              @click="loadReplies(comment.id)"
            >
              ↳ Vezi {{ comment.replies_count }} {{ comment.replies_count === 1 ? 'răspuns' : 'răspunsuri' }}
            </button>

            <!-- Replies sub-states (mutually exclusive) -->
            <template v-if="repliesState[comment.id]">
              <!-- Loading -->
              <div
                v-if="repliesState[comment.id].loading"
                class="poll-comments__state poll-comments__state--inline"
              >
                Se încarcă răspunsurile...
              </div>

              <!-- Error -->
              <div
                v-else-if="repliesState[comment.id].error"
                class="poll-comments__replies-error"
              >
                Nu s-au putut încărca răspunsurile.
                <button type="button" class="poll-comments__retry-btn" @click="loadReplies(comment.id)">Reîncearcă</button>
              </div>

              <!-- Loaded: replies list (or empty message) -->
              <template v-else>
                <div
                  v-if="repliesState[comment.id].replies.length === 0"
                  class="poll-comments__state poll-comments__state--inline"
                >
                  Nu există răspunsuri momentan.
                </div>
                <CommentItem
                  v-for="reply in repliesState[comment.id].replies"
                  :key="reply.id"
                  :comment="reply"
                  :poll-id="pollId"
                  :can-reply="hasVoted && pollIsActive"
                  :is-reply="true"
                  :can-delete="canDeleteComment(reply)"
                  :is-own="reply.user_id === currentUserId"
                  :current-user-id="currentUserId"
                  :is-reported-by-me="reply.is_reported_by_me || false"
                  :show-reply-editor="replyTarget && replyTarget.id === reply.id"
                  @reply="startReply"
                  @deleted="onCommentDeleted"
                  @reported="onCommentReported($event, 'reply', comment.id)"
                  @need-auth="$emit('need-auth')"
                >
                  <template v-if="replyTarget && replyTarget.id === reply.id" #reply-editor>
                    <div class="poll-comments__reply-form">
                      <div class="poll-comments__reply-quote">
                        {{ replyTarget.preview }}
                      </div>
                      <RichTextEditor v-model="replyText" />
                      <div class="poll-comments__form-actions">
                        <button type="button" class="poll-comments__cancel-btn" @click="cancelReply">Anulează</button>
                        <button
                          type="button"
                          class="poll-comments__submit-btn"
                          :disabled="isSubmitting || isTextEmpty(replyText)"
                          @click="submitReply(comment.id)"
                        >
                          {{ isSubmitting ? 'Se trimite...' : 'Publică răspunsul' }}
                        </button>
                      </div>
                      <p v-if="submitError" class="poll-comments__error">{{ submitError }}</p>
                    </div>
                  </template>
                </CommentItem>
              </template>
            </template>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import RichTextEditor from '../../../components/RichTextEditor.vue';
import CommentItem from './CommentItem.vue';
import recaptchaMixin from '../../../mixins/recaptchaMixin.js';

export default {
  name: 'PollComments',

  components: { RichTextEditor, CommentItem },

  mixins: [recaptchaMixin],

  emits: ['need-auth', 'reported'],

  props: {
    pollId: {
      type: [Number, String],
      required: true,
    },
    hasVoted: {
      type: Boolean,
      default: false,
    },
    // poll_option_id voted by current user (to tag comment with option)
    votedOptionId: {
      type: Number,
      default: null,
    },
    currentUserId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      comments: [],
      totalComments: 0,
      pollIsActive: false,
      // null = unknown (loading), false = not voted, true = voted
      userHasVoted: null,
      // Map: commentId → { loading: bool, replies: [], error: bool }
      repliesState: {},
      loadingComments: false,
      fetchCommentsError: false,
      newCommentText: '',
      // replyTarget: { id, username, preview, rootCommentId }
      replyTarget: null,
      replyText: '',
      isSubmitting: false,
      submitError: null,
    };
  },

  computed: {
    // True when the current user already has a top-level comment on this poll.
    userHasComment() {
      return this.pollIsActive && this.comments.some(c => c.user_id === this.currentUserId);
    },
  },

  mounted() {
    this.fetchComments();
  },

  watch: {
    pollId() {
      this.resetState();
      this.fetchComments();
    },
    hasVoted() {
      // Re-fetch whenever voting state changes (user voted or revoked vote)
      this.fetchComments();
    },
  },

  methods: {
    isTextEmpty(html) {
      if (!html) return true;
      return html.replace(/<[^>]*>/g, '').trim() === '';
    },

    resetState() {
      this.comments = [];
      this.totalComments = 0;
      this.pollIsActive = false;
      this.userHasVoted = null;
      this.repliesState = {};
      this.fetchCommentsError = false;
      this.newCommentText = '';
      this.replyTarget = null;
      this.replyText = '';
      this.submitError = null;
    },

    async fetchComments() {
      this.loadingComments = true;
      this.fetchCommentsError = false;
      this.userHasVoted = null;
      try {
        const response = await window.axios.get(`polls/${this.pollId}/comments`);
        if (typeof response.data !== 'object' || response.data === null) {
          throw new Error('Non-JSON response from comments endpoint');
        }
        this.comments = response.data.data || [];
        this.totalComments = response.data.total_comments || 0;
        this.pollIsActive = response.data.poll_is_active ?? false;
        this.userHasVoted = response.data.user_has_voted ?? null;
      } catch (error) {
        console.warn('Failed to load comments:', error);
        this.fetchCommentsError = true;
      } finally {
        this.loadingComments = false;
      }
    },

    async loadReplies(commentId) {
      // Use direct property assignment — Vue 3 Proxy tracks this correctly for new keys
      this.repliesState[commentId] = { loading: true, replies: [], error: false };
      try {
        const response = await window.axios.get(
          `polls/${this.pollId}/comments?parent_id=${commentId}`
        );
        // Guard: if the server returned HTML instead of JSON (e.g. stale route cache,
        // misconfiguration) axios won't throw but data will not be an object.
        if (typeof response.data !== 'object' || response.data === null) {
          throw new Error('Non-JSON response from replies endpoint');
        }
        this.repliesState[commentId] = {
          loading: false,
          replies: response.data.data || [],
          error: false,
        };
      } catch (error) {
        console.error(
          `Failed to load replies for comment ${commentId}:`,
          error?.response?.status,
          error?.response?.data || error?.message
        );
        this.repliesState[commentId] = { loading: false, replies: [], error: true };
      }
    },

    focusEditor() {
      this.$nextTick(() => {
        if (this.$refs.richEditor) {
          this.$refs.richEditor.focus();
        }
      });
    },

    canEditComment(comment) {
      if (!this.currentUserId || comment.user_id !== this.currentUserId) return false;
      // Same 10-minute window as delete — must match PollCommentController::destroy()
      if (new Date() - new Date(comment.created_at) >= 600000) return false;
      if (!comment.parent_id && comment.replies_count > 0) return false;
      return true;
    },

    canDeleteComment(comment) {
      if (!this.currentUserId || comment.user_id !== this.currentUserId) return false;
      // 10-minute window (600 000 ms) — must match PollCommentController::destroy()
      if (new Date() - new Date(comment.created_at) >= 600000) return false;
      if (!comment.parent_id && comment.replies_count > 0) return false;
      return true;
    },

    startReply(comment) {
      this.replyTarget = {
        id: comment.id,
        preview: this.stripHtml(comment.text, 115),
        rootCommentId: comment.root_comment_id ?? comment.id,
      };
      this.replyText = '';
      this.submitError = null;
    },

    cancelReply() {
      this.replyTarget = null;
      this.replyText = '';
      this.submitError = null;
    },

    stripHtml(html, maxLen) {
      const plain = (html || '').replace(/<[^>]*>/g, '').trim();
      if (plain.length <= maxLen) return plain;
      return plain.substring(0, maxLen) + '…';
    },

    async submitComment(parentId) {
      if (this.isTextEmpty(this.newCommentText) || this.isSubmitting) return;

      this.isSubmitting = true;
      this.submitError = null;

      try {
        const recaptchaToken = await this.getRecaptchaToken('comment');
        const payload = {
          text: this.newCommentText,
          parent_id: null,
          poll_option_id: this.votedOptionId || null,
          recaptcha_token: recaptchaToken,
        };

        const response = await window.axios.post(`polls/${this.pollId}/comments`, payload);
        const newComment = response.data.data;
        newComment.replies_count = 0;
        this.comments.unshift(newComment);
        this.newCommentText = '';
        this.submitError = null;
      } catch (error) {
        this.submitError = error?.response?.data?.message || 'A apărut o eroare. Încearcă din nou.';
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitReply(rootCommentId) {
      if (this.isTextEmpty(this.replyText) || this.isSubmitting || !this.replyTarget) return;

      this.isSubmitting = true;
      this.submitError = null;

      try {
        const recaptchaToken = await this.getRecaptchaToken('comment');
        const payload = {
          text: this.replyText,
          parent_id: this.replyTarget.id,
          poll_option_id: this.votedOptionId || null,
          recaptcha_token: recaptchaToken,
        };

        const response = await window.axios.post(`polls/${this.pollId}/comments`, payload);
        const newReply = response.data.data;

        // Increment replies_count on the root comment
        const rootComment = this.comments.find(c => c.id === rootCommentId);
        if (rootComment) {
          rootComment.replies_count = (rootComment.replies_count || 0) + 1;
        }

        // Add reply to the loaded replies list (if already loaded)
        if (this.repliesState[rootCommentId]) {
          this.repliesState[rootCommentId].replies.push(newReply);
        } else {
          // Auto-load replies so the new one is visible
          await this.loadReplies(rootCommentId);
        }

        this.cancelReply();
      } catch (error) {
        this.submitError = error?.response?.data?.message || 'A apărut o eroare. Încearcă din nou.';
      } finally {
        this.isSubmitting = false;
      }
    },

    onCommentEdited(updatedComment) {
      const idx = this.comments.findIndex(c => c.id === updatedComment.id);
      if (idx !== -1) {
        this.comments[idx] = updatedComment;
      }
    },

    onCommentDeleted(comment) {
      if (!comment.parent_id) {
        // Top-level: remove from main comments array
        this.comments = this.comments.filter(c => c.id !== comment.id);
      } else {
        // Reply: remove from repliesState and decrement root comment's replies_count
        const rootId = comment.root_comment_id;
        if (rootId && this.repliesState[rootId]) {
          this.repliesState[rootId].replies = this.repliesState[rootId].replies.filter(r => r.id !== comment.id);
        }
        const rootComment = this.comments.find(c => c.id === rootId);
        if (rootComment && rootComment.replies_count > 0) {
          rootComment.replies_count--;
        }
      }
    },

    onCommentReported(commentId, level, rootCommentId) {
      if (level === 'top') {
        const comment = this.comments.find(c => c.id === commentId);
        if (comment) comment.is_reported_by_me = true;
      } else if (level === 'reply' && rootCommentId && this.repliesState[rootCommentId]) {
        const reply = this.repliesState[rootCommentId].replies.find(r => r.id === commentId);
        if (reply) reply.is_reported_by_me = true;
      }
      this.$emit('reported');
    },
  },
};
</script>
